import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { PhoneCall, Plus, Play, Pause, XCircle, RefreshCw, Loader2, Bot, FlaskConical, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import { fetchCampaigns, startCampaign, pauseCampaign, cancelCampaign, deleteCampaign } from "../services/crmServices/campaignService";
import { CreateCampaignModal } from "../components/aiCaller/CreateCampaignModal";
import { CampaignDetail } from "../components/aiCaller/CampaignDetail";
import { TestCallModal } from "../components/aiCaller/TestCallModal";
import { LiveCallsPanel } from "../components/aiCaller/LiveCallsPanel";
import { ResultsInbox } from "../components/aiCaller/ResultsInbox";

const STATUS_STYLES = {
    draft: "bg-gray-100 text-gray-700",
    running: "bg-green-100 text-green-700",
    paused: "bg-yellow-100 text-yellow-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700"
};

export function AiCaller() {
    const { user } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTestCallModal, setShowTestCallModal] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [actionId, setActionId] = useState(null);
    const [activeTab, setActiveTab] = useState("campaigns"); // campaigns | live | results

    const role = user?.position?.role;
    const hasAccess = role === "admin" || role === "floorManager";
    const isAdmin = role === "admin";

    const load = useCallback(async () => {
        try {
            const res = await fetchCampaigns();
            setCampaigns(res?.data || []);
        } catch (err) {
            console.error("Failed to load campaigns", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (hasAccess) load();
    }, [hasAccess, load]);

    // Auto-refresh the list while any campaign is running
    useEffect(() => {
        if (selectedCampaignId || !campaigns.some((c) => c.status === "running")) return;
        const timer = setInterval(load, 15000);
        return () => clearInterval(timer);
    }, [campaigns, selectedCampaignId, load]);

    const doAction = async (campaignId, action, fn, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setActionId(campaignId);
        try {
            await fn(campaignId);
            toast.success(`Campaign ${action}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.message || `Failed to ${action} campaign`);
        } finally {
            setActionId(null);
        }
    };

    if (!hasAccess) {
        return (
            <div className="text-center py-20">
                <p className="text-sm text-gray-500">You don't have access to this page.</p>
            </div>
        );
    }

    if (selectedCampaignId) {
        return (
            <div className="p-4 md:p-6">
                <CampaignDetail
                    campaignId={selectedCampaignId}
                    onBack={() => { setSelectedCampaignId(null); load(); }}
                />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-100 rounded-xl">
                        <Bot className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">AI Caller</h1>
                        <p className="text-xs text-gray-500">Run outbound AI call campaigns with RetellAI</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={load}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowTestCallModal(true)}
                    >
                        <FlaskConical className="h-4 w-4 mr-1" /> Test Call
                    </Button>
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        size="sm"
                    >
                        <Plus className="h-4 w-4 mr-1" /> New Campaign
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
                {[
                    { id: "campaigns", label: "Campaigns" },
                    { id: "live", label: "Live Calls" },
                    { id: "results", label: "Results" }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                            activeTab === tab.id
                                ? "border-indigo-600 text-indigo-600"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === "live" && <LiveCallsPanel />}
            {activeTab === "results" && <ResultsInbox />}

            {/* Campaign list */}
            {activeTab === "campaigns" && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="p-3 bg-gray-50 rounded-full mb-3">
                            <PhoneCall className="h-8 w-8 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-600">No campaigns yet</p>
                        <p className="text-xs text-gray-400 mt-1">Create your first AI call campaign to get started</p>
                        <p className="text-xs text-gray-400 mt-0.5">Make sure RetellAI is configured in Settings first</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50/80">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Campaign</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Audience</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Progress</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {campaigns.map((campaign) => {
                                    const totals = campaign.totals || {};
                                    const total = (totals.queued || 0) + (totals.calling || 0) + (totals.completed || 0) + (totals.failed || 0);
                                    const done = (totals.completed || 0) + (totals.failed || 0);
                                    const progress = total > 0 ? Math.round((done / total) * 100) : 0;
                                    const busy = actionId === campaign._id;
                                    return (
                                        <tr
                                            key={campaign._id}
                                            className="hover:bg-gray-50/50 cursor-pointer"
                                            onClick={() => setSelectedCampaignId(campaign._id)}
                                        >
                                            <td className="px-4 py-3">
                                                <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                                                {campaign.description && <p className="text-xs text-gray-400 truncate max-w-[240px]">{campaign.description}</p>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge className={`text-xs ${STATUS_STYLES[campaign.status] || ""}`}>{campaign.status}</Badge>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{total} leads</td>
                                            <td className="px-4 py-3 w-48">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${progress}%` }} />
                                                    </div>
                                                    <span className="text-xs text-gray-500 w-16 text-right">{done}/{total}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-500">
                                                {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1">
                                                    {(campaign.status === "draft" || campaign.status === "paused") && (
                                                        <Button
                                                            variant="ghost" size="sm" disabled={busy}
                                                            onClick={() => doAction(campaign._id, "started", startCampaign)}
                                                            title="Start"
                                                        >
                                                            <Play className="h-4 w-4 text-green-600" />
                                                        </Button>
                                                    )}
                                                    {campaign.status === "running" && (
                                                        <Button
                                                            variant="ghost" size="sm" disabled={busy}
                                                            onClick={() => doAction(campaign._id, "paused", pauseCampaign)}
                                                            title="Pause"
                                                        >
                                                            <Pause className="h-4 w-4 text-yellow-600" />
                                                        </Button>
                                                    )}
                                                    {["draft", "running", "paused"].includes(campaign.status) && (
                                                        <Button
                                                            variant="ghost" size="sm" disabled={busy}
                                                            onClick={() => doAction(campaign._id, "cancelled", cancelCampaign, "Cancel this campaign? Queued calls will not be made.")}
                                                            title="Cancel"
                                                        >
                                                            <XCircle className="h-4 w-4 text-red-500" />
                                                        </Button>
                                                    )}
                                                    {isAdmin && campaign.status !== "running" && (
                                                        <Button
                                                            variant="ghost" size="sm" disabled={busy}
                                                            onClick={() => doAction(campaign._id, "deleted", deleteCampaign, "Delete this campaign permanently? Its call history will be removed. The leads themselves are kept.")}
                                                            title="Delete campaign"
                                                        >
                                                            <Trash2 className="h-4 w-4 text-red-600" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            )}

            <CreateCampaignModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => load()}
            />

            <TestCallModal
                open={showTestCallModal}
                onClose={() => setShowTestCallModal(false)}
            />
        </div>
    );
}
